import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDirtyForm, ConfirmDialog } from '@viilutalous/common';

/**
 * Intercepts react-router navigation (push/replace) when a form is dirty.
 * Works with BrowserRouter by monkey-patching the navigator from UNSAFE_NavigationContext.
 * Browser tab close/refresh is handled by DirtyFormProvider's beforeunload listener.
 */
export default function NavigationBlocker() {
  const { isDirty } = useDirtyForm();
  const { t } = useTranslation();
  const { navigator } = useContext(UNSAFE_NavigationContext);

  const [showDialog, setShowDialog] = useState(false);
  const pendingRef = useRef(null);
  const origPushRef = useRef(null);
  const origReplaceRef = useRef(null);

  useEffect(() => {
    if (!isDirty) return;

    origPushRef.current = navigator.push;
    origReplaceRef.current = navigator.replace;

    navigator.push = (...args) => {
      pendingRef.current = { method: 'push', args };
      setShowDialog(true);
    };

    navigator.replace = (...args) => {
      pendingRef.current = { method: 'replace', args };
      setShowDialog(true);
    };

    return () => {
      navigator.push = origPushRef.current;
      navigator.replace = origReplaceRef.current;
    };
  }, [isDirty, navigator]);

  const handleConfirm = useCallback(() => {
    setShowDialog(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;

    const origFn = pending.method === 'push' ? origPushRef.current : origReplaceRef.current;
    origFn?.(...pending.args);
  }, []);

  const handleCancel = useCallback(() => {
    setShowDialog(false);
    pendingRef.current = null;
  }, []);

  return (
    <ConfirmDialog
      open={showDialog}
      message={t('common.unsaved_changes_confirm')}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
}
